/**
 * Registration Form Component
 *
 * Handles email/password registration with inline validation.
 * Uses the registration API endpoint and shows loading states.
 */

import * as React from "react";
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@provenleap/ui";
import { PasswordInput } from "./PasswordInput";
import { LoadingSpinner } from "../icons";

interface RegisterFormProps {
  onSuccess?: (data: { userId: string; tenantId: string }) => void;
  onError?: (error: string) => void;
  apiBaseUrl?: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

interface PasswordValidation {
  minLength: boolean;
  uppercase: boolean;
  number: boolean;
}

export function RegisterForm({
  onSuccess,
  onError,
  apiBaseUrl = "",
}: RegisterFormProps) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [touched, setTouched] = React.useState<{
    email: boolean;
    password: boolean;
    confirmPassword: boolean;
  }>({
    email: false,
    password: false,
    confirmPassword: false,
  });

  // Password validation state
  const passwordValidation: PasswordValidation = React.useMemo(() => {
    return {
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
    };
  }, [password]);

  // Validate email format
  const validateEmail = (value: string): string | undefined => {
    if (!value) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
    return undefined;
  };

  // Validate password
  const validatePassword = (value: string): string | undefined => {
    if (!value) return "Password is required";
    if (value.length < 8) return "8+ characters required";
    if (!/[A-Z]/.test(value))
      return "1 uppercase letter required";
    if (!/[0-9]/.test(value))
      return "1 number required";
    return undefined;
  };

  // Validate confirm password
  const validateConfirmPassword = (
    value: string,
    originalPassword: string
  ): string | undefined => {
    if (!value) return "Please confirm your password";
    if (value !== originalPassword) return "Passwords do not match";
    return undefined;
  };

  // Handle field blur for touched state
  const handleBlur = (field: "email" | "password" | "confirmPassword") => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    // Validate on blur
    if (field === "email") {
      const error = validateEmail(email);
      setErrors((prev) => ({ ...prev, email: error }));
    } else if (field === "password") {
      const error = validatePassword(password);
      setErrors((prev) => ({ ...prev, password: error }));
    } else if (field === "confirmPassword") {
      const error = validateConfirmPassword(confirmPassword, password);
      setErrors((prev) => ({ ...prev, confirmPassword: error }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    const confirmPasswordError = validateConfirmPassword(confirmPassword, password);

    setErrors({
      email: emailError,
      password: passwordError,
      confirmPassword: confirmPasswordError,
    });

    // Mark all as touched
    setTouched({ email: true, password: true, confirmPassword: true });

    // If any errors, don't submit
    if (emailError || passwordError || confirmPasswordError) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors from API
        if (data.errors && Array.isArray(data.errors)) {
          const apiErrors: FormErrors = {};
          for (const err of data.errors) {
            if (err.field === "email") apiErrors.email = err.message;
            if (err.field === "password") apiErrors.password = err.message;
          }
          setErrors(apiErrors);
        } else if (data.type?.includes("email-exists")) {
          setErrors({ email: "An account with this email already exists" });
        } else {
          setErrors({
            general: data.detail || data.title || "Registration failed. Please try again.",
          });
        }
        onError?.(data.title || "Registration failed");
        return;
      }

      // Success
      onSuccess?.({ userId: data.data.userId, tenantId: data.data.tenantId });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Network error";
      setErrors({ general: "Unable to connect. Please try again." });
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader className="pb-4">
        <CardTitle>Create Account</CardTitle>
        <CardDescription className="text-neutral-700">
          Sign up for ProvenLeap to get started
        </CardDescription>
        <div className="relative mt-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-neutral-700/20" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-neutral-700">REGISTRATION FORM</span>
          </div>
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-2">
          {/* General error message */}
          {errors.general && (
            <div className="rounded-md bg-error/10 p-3 text-sm text-error">
              {errors.general}
            </div>
          )}

          {/* Email field */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              {touched.email && errors.email && (
                <p className="text-sm text-error">{errors.email}</p>
              )}
            </div>
            <Input
              id="email"
              type="email"
              className="rounded-lg"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur("email")}
              aria-invalid={touched.email && !!errors.email}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          {/* Password field */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              {touched.password && errors.password && (
                <p className="text-sm text-error">{errors.password}</p>
              )}
            </div>
            <PasswordInput
              id="password"
              placeholder="Create a strong password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (touched.password) {
                  const error = validatePassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: error }));
                }
              }}
              onBlur={() => handleBlur("password")}
              aria-invalid={touched.password && !!errors.password}
              disabled={isLoading}
              autoComplete="new-password"
            />
            {/* Password requirements */}
            {(touched.password || password.length > 0) && (
              <div className="relative !mt-2 animate-slide-down">
                {/* Arrow pointing up */}
                <div className="absolute -top-[6px] left-4 w-3 h-3 bg-white border-l border-t border-border rotate-45 z-10" />
                <ul className="relative space-y-1 text-[14px] rounded-md border border-border bg-white p-3 z-0 shadow-[0_0_10px_rgba(0,0,0,0.1)]">
                  <li
                    className={
                      passwordValidation.minLength
                        ? "text-green-600"
                        : "text-neutral-600"
                    }
                  >
                    {passwordValidation.minLength ? "✓" : "○"} 8+ characters required
                  </li>
                  <li
                    className={
                      passwordValidation.uppercase
                        ? "text-green-600"
                        : "text-neutral-600"
                    }
                  >
                    {passwordValidation.uppercase ? "✓" : "○"} 1 uppercase letter required
                  </li>
                  <li
                    className={
                      passwordValidation.number
                        ? "text-green-600"
                        : "text-neutral-600"
                    }
                  >
                    {passwordValidation.number ? "✓" : "○"} 1 number required
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm password field */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirm Password
              </label>
              {touched.confirmPassword && errors.confirmPassword && (
                <p className="text-sm text-error">{errors.confirmPassword}</p>
              )}
            </div>
            <PasswordInput
              id="confirmPassword"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => handleBlur("confirmPassword")}
              aria-invalid={touched.confirmPassword && !!errors.confirmPassword}
              disabled={isLoading}
              autoComplete="new-password"
            />
            {/* Passwords match indicator */}
            {(touched.confirmPassword || confirmPassword.length > 0) && (
              <div className="relative !mt-2 animate-slide-down">
                {/* Arrow pointing up */}
                <div className="absolute -top-[6px] left-4 w-3 h-3 bg-white border-l border-t border-border rotate-45 z-10" />
                <div className="relative text-[14px] rounded-md border border-border bg-white p-3 z-0 shadow-[0_0_10px_rgba(0,0,0,0.1)]">
                  <p
                    className={
                      confirmPassword.length > 0 && password === confirmPassword
                        ? "text-green-600"
                        : "text-neutral-600"
                    }
                  >
                    {confirmPassword.length > 0 && password === confirmPassword ? "✓" : "○"} Passwords match
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button type="submit" variant="brand" className="w-full cursor-pointer" disabled={isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
          <p className="text-sm text-neutral-700 text-center">
            Already have an account?{" "}
            <a href="/login" className="font-medium text-primary hover:underline">
              Log in
            </a>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
